import React, {useMemo} from 'react';
import {validateLuceneQuerySyntax} from '@/views/storyboard/utils/validateLuceneQuerySyntax';
import i18n from 'i18n';

const EXCERPT_CONTEXT = 28;

const QuerySyntaxExcerpt = React.memo<{
    query: string;
    start: number;
    end: number;
}>(({query, start, end}) => {
    const excerptStart = Math.max(0, start - EXCERPT_CONTEXT);
    const excerptEnd = Math.min(query.length, Math.max(end, start + 1) + EXCERPT_CONTEXT);
    const highlightStart = start - excerptStart;
    const highlightEnd = Math.max(highlightStart + 1, end - excerptStart);

    return (
        <pre className="chart-query-editor__syntax-excerpt">
            {excerptStart > 0 ? '…' : null}
            {query.slice(excerptStart, excerptStart + highlightStart)}
            <mark className="chart-query-editor__syntax-highlight">
                {query.slice(start, Math.max(end, start + 1))}
            </mark>
            {query.slice(excerptStart + highlightEnd, excerptEnd)}
            {excerptEnd < query.length ? '…' : null}
        </pre>
    );
});

interface Props {
    query: string;
    className?: string;
}

const ChartQuerySyntaxFeedback = React.memo<Props>(({query, className = ''}) => {
    const syntaxValidation = useMemo(() => validateLuceneQuerySyntax(query), [query]);

    if (!syntaxValidation.issues.length) {
        return null;
    }

    return (
        <div className={`chart-query-editor__syntax-issues${className ? ` ${className}` : ''}`}>
            {syntaxValidation.issues.map((issue) => (
                <div
                    className="chart-query-editor__syntax-issue"
                    key={`${issue.start}-${issue.end}-${issue.message}`}
                >
                    <div className="chart-query-editor__error">
                        {issue.message}
                    </div>
                    <div className="chart-query-editor__syntax-location">
                        <span className="chart-query-editor__syntax-location-label">
                            {i18n.t('chart_settings_query_syntax_location.text')}
                        </span>
                        <QuerySyntaxExcerpt
                            query={query}
                            start={issue.start}
                            end={issue.end}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
});

export const useQuerySyntaxValidation = (query: string) => useMemo(
    () => validateLuceneQuerySyntax(query),
    [query],
);

export default ChartQuerySyntaxFeedback;
